import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationContractAlertsContentFiltersAlertsContentFiltersBody } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { useToasts } from "app/common/hooks";
import { useApiMutation } from "app/modules/api-client";
import type { InvalidateQueriesData } from "app/modules/api-client/utils";

type UseContentFiltersMutationParams = {
    activationId: string;
    hasExistingData: boolean;
};

export const useContentFiltersMutation = ({ activationId, hasExistingData }: UseContentFiltersMutationParams) => {
    const { showErrorToast } = useToasts();
    const { translate } = useTranslate();
    const invalidateQueries = {
        Alerts: true,
    } satisfies InvalidateQueriesData;

    const { mutateAsync: createContentFilters, isPending: isCreatePending } = useApiMutation(
        "Alerts",
        "userdataActivationAlertsContentFiltersCreate",
        {
            invalidateQueries,
            onError: (error, variables) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Error in createContentFilters mutation",
                    level: "error",
                    params: {
                        extra: {
                            hasExistingData,
                            variables,
                        },
                    },
                });

                showErrorToast(translate("common.api.error", { error: error.message }));
            },
        },
    );
    const { mutateAsync: updateContentFilters, isPending: isUpdatePending } = useApiMutation(
        "Alerts",
        "userdataActivationAlertsContentFiltersUpdate",
        {
            invalidateQueries,
            onError: (error, variables) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Error in updateContentFilters mutation",
                    level: "error",
                    params: {
                        extra: {
                            hasExistingData,
                            variables,
                        },
                    },
                });

                showErrorToast(translate("common.api.error", { error: error.message }));
            },
        },
    );
    const { mutateAsync: deleteContentFilters, isPending: isDeletePending } = useApiMutation(
        "Alerts",
        "userdataActivationAlertsContentFiltersDelete",
        {
            invalidateQueries,
            onError: (error, variables) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Error in deleteContentFilters mutation",
                    level: "error",
                    params: {
                        extra: {
                            hasExistingData,
                            variables,
                        },
                    },
                });

                showErrorToast(translate("common.api.error", { error: error.message }));
            },
        },
    );

    const mutateAsync = useCallback(
        async (filters: ActivationContractAlertsContentFiltersAlertsContentFiltersBody["filters"]) => {
            const hasNewData = filters.length > 0;

            if (!hasExistingData && hasNewData) {
                await createContentFilters([activationId, { filters }]);
                return;
            }

            if (hasExistingData && hasNewData) {
                await updateContentFilters([activationId, { filters }]);
                return;
            }

            if (hasExistingData && !hasNewData) {
                await deleteContentFilters([activationId]);
            }
        },
        [activationId, hasExistingData, createContentFilters, updateContentFilters, deleteContentFilters],
    );

    return {
        mutateAsync,
        isPending: isCreatePending || isUpdatePending || isDeletePending,
    };
};
